import _trimEnd from 'lodash-es/trimEnd';

import type { ThemeFontWeight } from '@rover/kibble/styles/themeTypes';
import { IconColorEnum } from '@rover/rsdk/src/apiClient/latest/model/iconColorEnum';
import { TextColorEnum } from '@rover/rsdk/src/apiClient/latest/model/textColorEnum';
import { isProdNodeEnv } from '@rover/rsdk/src/modules/Env/node';
import { Logger } from '@rover/rsdk/src/modules/Logging';
import type { RoverTheme } from '@rover/types/src/RoverTheme';

export function getNativeStyleFromArray(
  value: number | string | [string, string] | [string, string, string]
): string | number {
  if (!Array.isArray(value)) {
    return value;
  }
  return value[0];
}

export function pxToNumber(value: string | number): number {
  if (typeof value === 'number') return value;
  if (!value || !value.endsWith('px')) return 0;
  return parseInt(_trimEnd(value, 'px'), 10);
}

/** A `text`/`paragraph`/`heading` entry from the theme's typography scales. */
export type ThemeTypographyStyle =
  | RoverTheme['text'][keyof RoverTheme['text']]
  | RoverTheme['paragraph'][keyof RoverTheme['paragraph']]
  | RoverTheme['heading'][keyof RoverTheme['heading']];

/**
 * Resolves a typography token's line height to absolute pixels.
 *
 * The web theme stores `lineHeight` as a unitless multiplier (`1.5`) while the native theme
 * stores it pre-multiplied as px (`'24px'`), so callers can't rely on `pxToNumber` alone.
 */
export function lineHeightPx(style: ThemeTypographyStyle): number {
  const { fontSize, lineHeight } = style;
  return typeof lineHeight === 'number'
    ? pxToNumber(fontSize) * lineHeight
    : pxToNumber(lineHeight);
}

export type AvertaFontFamilyNames = 'Averta-Regular' | 'Averta-Semibold';
const convertFontWeight = (weight?: ThemeFontWeight): AvertaFontFamilyNames => {
  if (weight === 'semibold') {
    return 'Averta-Semibold';
  }
  return 'Averta-Regular';
};
export function fontWeightToFontFamily(
  weight?: ThemeFontWeight | ThemeFontWeight[]
): AvertaFontFamilyNames | AvertaFontFamilyNames[] {
  if (Array.isArray(weight)) {
    return weight.map(convertFontWeight);
  }
  return convertFontWeight(weight);
}
/**
 * Note: TextColorEnum and IconColorEnum are identical enums but exist as separate types
 * due to how they are generated from the API schema.
 * In the backend, both are mapped to the TextColor enum from the thin API.
 * This allows the getApiColor function to accept both types while maintaining type safety.
 */
export function getApiColor(color: TextColorEnum | IconColorEnum, theme: RoverTheme): string {
  if (!color) {
    Logger.warn(
      `getApiColor called with empty color: ${color}. Returning primary color as fallback.`
    );
    return theme.textColor.primary.toString();
  }
  switch (color) {
    case TextColorEnum.TEXT_COLOR_PRIMARY:
      return theme.textColor.primary.toString();
    case TextColorEnum.TEXT_COLOR_PRIMARY_INVERSE:
      return theme.textColor.primary_inverse.toString();
    case TextColorEnum.TEXT_COLOR_SECONDARY:
      return theme.textColor.secondary.toString();
    case TextColorEnum.TEXT_COLOR_SECONDARY_INVERSE:
      return theme.textColor.secondary_inverse.toString();
    case TextColorEnum.TEXT_COLOR_TERTIARY:
      return theme.textColor.tertiary.toString();
    case TextColorEnum.TEXT_COLOR_TERTIARY_INVERSE:
      return theme.textColor.tertiary_inverse.toString();
    case TextColorEnum.TEXT_COLOR_PLACEHOLDER:
      return theme.textColor.placeholder.toString();
    case TextColorEnum.TEXT_COLOR_DISABLED:
      return theme.textColor.disabled.toString();
    case TextColorEnum.TEXT_COLOR_PRICE:
      return theme.textColor.price.toString();
    case TextColorEnum.TEXT_COLOR_ERROR:
      return theme.textColor.error.toString();
    case TextColorEnum.TEXT_COLOR_CAUTION:
      return theme.textColor.caution.toString();
    case TextColorEnum.TEXT_COLOR_SUCCESS:
      return theme.textColor.success.toString();
    default:
      Logger.warn(`Unknown color: ${color}. Returning primary color as fallback.`);
      return theme.textColor.primary.toString();
  }
}

/**
 * Resolves the `BORDER_COLOR_*` tokens the API sends, which `getApiColor` (`TEXT_COLOR_*`) can't.
 */
export function getApiBorderColor(color: string, theme: RoverTheme): string {
  switch (color) {
    case 'BORDER_COLOR_SUCCESS':
      return theme.colors.border.success.toString();
    case 'BORDER_COLOR_CAUTION':
      return theme.colors.border.caution.toString();
    case 'BORDER_COLOR_ERROR':
      return theme.colors.border.error.toString();
    case 'BORDER_COLOR_PRIMARY':
      return theme.colors.border.primary.toString();
    case 'BORDER_COLOR_SECONDARY':
      return theme.colors.border.secondary.toString();
    case 'BORDER_COLOR_CONTRAST':
      return theme.colors.border.contrast.toString();
    case 'BORDER_COLOR_INFO':
      return theme.colors.border.info.toString();
    case 'BORDER_COLOR_HIGHLIGHT':
      return theme.colors.border.highlight.toString();
    case 'BORDER_COLOR_INPUT_FOCUS':
      return theme.colors.border.input_focus.toString();
    default:
      Logger.warn(`Unknown border color: ${color}. Returning primary border color as fallback.`);
      return theme.colors.border.primary.toString();
  }
}

export function withOpacity(color: string, opacity: number): string {
  const percent = Math.round(opacity * 100);
  return `color-mix(in srgb, ${color} ${percent}%, transparent)`;
}

const HEX_COLOR = /^#([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i;

/**
 * Returns an rgba color for a 6-digit hex token. This is useful when color-mix
 * is not supported by the target browser or platform.
 */
export function withOpacityRgba(color: string, opacity: number): string {
  const match = HEX_COLOR.exec(color);

  if (!match) {
    Logger.warn(`withOpacityRgba expects a 6-digit hex color, got: ${color}.`);
    return color;
  }

  const [, r, g, b] = match;
  return `rgba(${parseInt(r, 16)}, ${parseInt(g, 16)}, ${parseInt(b, 16)}, ${opacity})`;
}

export function withOpacityNative(color: string, opacity: number): string {
  return withOpacityRgba(color, opacity);
}

/**
 * Reports a component-contract violation. In development it throws so misuse fails
 * loudly; in production it logs to Splunk via `Logger.warn` by default. Pass
 * `reportToSentry` for the rare violation that genuinely warrants Sentry alerting
 * (`Logger.error`) rather than a Splunk-only warning.
 */
export function throwOrLogError(
  errorText: string,
  { reportToSentry = false }: { reportToSentry?: boolean } = {}
): void {
  if (!isProdNodeEnv()) {
    throw new Error(errorText);
  }
  if (reportToSentry) {
    Logger.error(new Error(errorText));
  } else {
    // Preserve the stack so Splunk can still pinpoint the offending call site,
    // which is the whole reason these assertions exist.
    Logger.warn(errorText, { stack: new Error(errorText).stack ?? '' });
  }
}
